import { Injectable } from '@angular/core';
import { DataViewTypes, FlamingoTypes, SwitchViewDomain } from '@mode/shared/contract-common';
import { SwitchViewClientService } from '@mode/shared/data-access-webapp';
import { getFormattedDuration, isPresent } from '@mode/shared/util-js';
import { DataViewFacade, SwitchAttrsActions, SwitchViewActions } from '@mode/switch-views/contract';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, filter, first, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import {
  createQueryRunTokenSelectorByQueryToken,
  getBytesSizeBySwitchViewToken,
  getCsvUrlBySwitchViewToken,
  getDatasetsLimitExceeded,
  getDatasetResultByQueryRunToken,
  getDatasetResultByQueryToken,
  getDatasetResultByToken,
  getDatasetResultCompletedAtBySwitchViewToken,
  getDatasetResults,
  getDatasetWithOneSwitchView,
  getDatasetWithRemovalFail,
  getErrorByQueryToken,
  getJsonUrlBySwitchViewToken,
  getLegacyContentBySwitchViewToken,
  getMetaColumnsBySwitchViewToken,
  getQueryNameBySwitchViewToken,
  getQueryRunStateByQueryToken,
  getQueryRunStateBySwitchViewToken,
  getQueryTokenBySwitchViewToken,
  getRawQueryByQueryToken,
  getRawQueryBySwitchViewToken,
  getRenderedQueryByQueryToken,
  getRenderedQueryByViewToken,
  getRowCountBySwitchViewToken,
  getSuccessfulDatasets,
  getSwitchViewTokenByQueryToken,
  getPublishDatasetResultBySwitchViewToken,
  isPublishedDatasetBySwitchViewToken,
} from './dataset-result/dataset-result.selectors';
import {
  createColumnsAndAttrsSwitchModelByQueryToken,
  getLocalSwitchViewAttrFeatures,
  getSwitchViewAttrHasCalc,
} from './switch-attrs/switch-attrs.selectors';
import { DomainAppState } from './switch-view-domain.types';
import {
  createColumnsSwitchModelSelectorByQueryToken,
  createRootSourceModelNameSelectorBySwitchViewToken,
  createRunReadyOrRunningSelectorByQueryToken,
  createRunReadySelectorByQueryToken,
  getEnhancedDatasetResultBySwitchViewToken,
  getEnhancedDatasetResults,
  getImportedDatasetCount,
  getImportedDatasetResults,
  getLocalSwitchViewTokens,
  getModel,
  getModelFormatsByQueryToken,
  getRunReadyWithFailedSelectorByQueryToken,
  getSuccessfulEnhancedDatasets,
  getSuccessfulQueries,
  getSwitchViewByToken,
  getSwitchViewFeatureByToken,
  getSwitchViewTokens,
  getSwitchViewsExist,
  switchViewsLoaded,
} from './switch-view/switch-view.selectors';

@Injectable()
export class DataViewService implements DataViewFacade {
  constructor(
    private store: Store<DomainAppState>,
    private actions$: Actions,
    private switchViewClientService: SwitchViewClientService
  ) {
    this.model$ = this.store.select(getModel).pipe(shareReplay(1));

    this.datasetResults$ = this.store.select(getDatasetResults).pipe(shareReplay(1));

    this.successfulDatasets$ = this.store.select(getSuccessfulDatasets).pipe(shareReplay(1));

    this.successfulEnhancedDatasets$ = this.store.select(getSuccessfulEnhancedDatasets).pipe(shareReplay(1));

    this.importedDatasetCount$ = this.store.select(getImportedDatasetCount).pipe(shareReplay(1));

    this.importedDatasetResults$ = combineLatest([
      this.store.select(getImportedDatasetResults),
      this.store.select(switchViewsLoaded),
    ]).pipe(
      filter(([_, loaded]) => loaded),
      map(([data]) => data)
    );

    this.enhancedDatasetResults$ = this.store.select(getEnhancedDatasetResults).pipe(shareReplay(1));

    this.successfulEnhancedQueries$ = this.store.select(getSuccessfulQueries).pipe(shareReplay(1));

    this.localSwitchViewTokens$ = this.store.select(getLocalSwitchViewTokens).pipe(shareReplay(1));

    this.localSwitchViewCount$ = this.localSwitchViewTokens$.pipe(map((tokens) => tokens?.length || 0));

    this.isRemoveDatasetError$ = this.store.select(getDatasetWithRemovalFail).pipe(shareReplay(1));

    this.canDeleteSwitchView$ = this.store.select(getDatasetWithOneSwitchView).pipe(shareReplay(1));

    this.getDatasetsLimitExceeded$ = this.store.select(getDatasetsLimitExceeded).pipe(shareReplay(1));

    this.switchViewTokens$ = this.store.select(getSwitchViewTokens);

    this.attrUpdated$ = this.actions$.pipe(
      ofType(SwitchAttrsActions.updateSwitchViewAttrSuccess),
      map((action) => ({
        switchViewToken: action.attr.switchViewToken,
        attrToken: action.token,
      }))
    );

    // This is a temporary solution to track when report editor GF state has loaded, since this needs
    // to be added to the loaded$ context for reports. In the future, this type of thing should be
    // refactored to ngrx state, though it would probably interact with the report feature which
    // we don't yet have defined.
    //
    // This determines if an initial switchView load for a report has happened by assuming that switchViews
    // must be present for a report. As more of the GF editor state refactor happens, this solution should
    // be migrated out to be handled in state.
    const switchViewsExist$ = this.store.select(getSwitchViewsExist);
    this.initialEditorSwitchViewsLoaded$ = merge(
      switchViewsExist$.pipe(first()),
      actions$.pipe(
        ofType(SwitchViewActions.loadEditorViewsSuccess),
        map(() => true)
      )
    ).pipe(distinctUntilChanged(), shareReplay(1));
  }

  /**
   * Observables
   */

  model$: Observable<FlamingoTypes.ModeModel>;

  datasetResults$: Observable<DataViewTypes.DatasetResult[]>;

  successfulDatasets$: Observable<DataViewTypes.DatasetResult[]>;

  successfulEnhancedDatasets$: Observable<DataViewTypes.EnhancedDatasetResult[]>;

  importedDatasetCount$: Observable<number>;

  importedDatasetResults$: Observable<DataViewTypes.DatasetResult[]>;

  enhancedDatasetResults$: Observable<DataViewTypes.EnhancedDatasetResult[]>;

  successfulEnhancedQueries$: Observable<DataViewTypes.EnhancedDatasetResult[]>;

  localSwitchViewTokens$: Observable<string[]>;

  localSwitchViewCount$: Observable<number>;

  attrUpdated$: Observable<{ switchViewToken: string | undefined; attrToken: string }>;

  initialEditorSwitchViewsLoaded$: Observable<boolean>;

  isRemoveDatasetError$: Observable<DataViewTypes.DatasetResult>;

  canDeleteSwitchView$: Observable<boolean>;

  getDatasetsLimitExceeded$: Observable<boolean>;

  switchViewTokens$: Observable<string[] | number[]>;

  /**
   * Observable creators
   */

  createDatasetBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getDatasetResultByToken(switchViewToken));
  }

  createEnhancedDatasetResultBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getEnhancedDatasetResultBySwitchViewToken(switchViewToken));
  }

  createSwitchViewFeatureBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getSwitchViewFeatureByToken(switchViewToken));
  }

  createRunReadyByQueryToken(queryToken: string) {
    return this.store.select(createRunReadySelectorByQueryToken(queryToken));
  }

  createRunReadyOrRunningByQueryToken(queryToken: string) {
    return this.store.select(createRunReadyOrRunningSelectorByQueryToken(queryToken));
  }

  createColumnsSwitchModelByQueryToken(queryToken: string) {
    return this.store.select(createColumnsSwitchModelSelectorByQueryToken(queryToken));
  }

  createColumnsAndAttrsSwitchModelByQueryToken(queryToken: string, switchViewToken: string) {
    return this.store.select(createColumnsAndAttrsSwitchModelByQueryToken(queryToken, switchViewToken));
  }

  createSwitchViewBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getSwitchViewByToken(switchViewToken));
  }

  createQueryRunTokenByQueryToken(queryToken: string) {
    return this.store.select(createQueryRunTokenSelectorByQueryToken(queryToken));
  }

  createQueryTokenBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getQueryTokenBySwitchViewToken(switchViewToken));
  }

  createSwitchViewTokenByQueryToken(queryToken: string) {
    return this.store.select(getSwitchViewTokenByQueryToken(queryToken));
  }

  // TODO: (@anthonysimone) [GF] - What would we prefer to do here? Have 2 helpers 1 with noEmpty and the other without,
  // or standardize on one or the other and adjust observables accordingly?
  createQueryRunTokenByQueryTokenNoEmpty(queryToken: string) {
    return this.store.select(createQueryRunTokenSelectorByQueryToken(queryToken)).pipe(filter(isPresent));
  }

  createRootSourceModelNameBySwitchViewToken(switchViewToken: string) {
    return this.store
      .select(createRootSourceModelNameSelectorBySwitchViewToken(switchViewToken))
      .pipe(filter(isPresent));
  }

  createRunReadyWithFailedByQueryToken(queryToken: string) {
    return this.store.select(getRunReadyWithFailedSelectorByQueryToken(queryToken));
  }

  createQueryRunStateByQueryTokenNoEmpty(queryToken: string) {
    return this.store.select(getQueryRunStateByQueryToken(queryToken)).pipe(filter(isPresent));
  }

  getDatasetResultCompletedAtBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getDatasetResultCompletedAtBySwitchViewToken(switchViewToken));
  }

  getBytesSizeBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getBytesSizeBySwitchViewToken(switchViewToken));
  }

  getCsvUrlBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getCsvUrlBySwitchViewToken(switchViewToken));
  }

  getLegacyContentBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getLegacyContentBySwitchViewToken(switchViewToken));
  }

  getMetaColumnsBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getMetaColumnsBySwitchViewToken(switchViewToken));
  }

  getRawQueryBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getRawQueryBySwitchViewToken(switchViewToken));
  }

  getRawQueryByQueryToken(queryToken: string) {
    return this.store.select(getRawQueryByQueryToken(queryToken));
  }

  getRenderedQueryByViewToken(switchViewToken: string) {
    return this.store.select(getRenderedQueryByViewToken(switchViewToken));
  }

  getRenderedQueryByQueryToken(queryToken: string) {
    return this.store.select(getRenderedQueryByQueryToken(queryToken));
  }

  getErrorByQueryToken(queryToken: string) {
    return this.store.select(getErrorByQueryToken(queryToken));
  }

  getRowCountBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getRowCountBySwitchViewToken(switchViewToken));
  }

  getQueryRunStateBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getQueryRunStateBySwitchViewToken(switchViewToken));
  }

  // TODO: Refactor to not be specific to query sources
  getDatasetResultByQuery(token: string) {
    return this.store.select(getDatasetResultByQueryToken(token));
  }

  getPublishDatasetResultBySwitchView(token: string) {
    return this.store.select(getPublishDatasetResultBySwitchViewToken(token));
  }

  isPublishedDatasetBySwitchView(token: string) {
    return this.store.select(isPublishedDatasetBySwitchViewToken(token));
  }

  getDatasetResultByQueryNoEmpty(token: string) {
    return this.store.select(getDatasetResultByQueryToken(token)).pipe(filter(isPresent));
  }

  getDatasetResultByQueryRunToken(queryRunToken: string) {
    return this.store.select(getDatasetResultByQueryRunToken(queryRunToken));
  }

  getLocalSwitchViewAttrFeaturesBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getLocalSwitchViewAttrFeatures(switchViewToken));
  }

  isCalcBySwitchViewTokenFromSwitchViewAttr(switchViewToken: string) {
    return this.store.select(getSwitchViewAttrHasCalc(switchViewToken));
  }

  getExecutionTimeBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getDatasetResultByToken(switchViewToken)).pipe(
      filter(isPresent),
      filter((entity) => entity.completedAt != null && entity.createdAt != null),
      map((entity) =>
        getFormattedDuration(Math.max(0, new Date(entity.completedAt).getTime() - new Date(entity.createdAt).getTime()))
      )
    );
  }

  getSwitchViewNameBySwitchViewToken(switchViewToken: string) {
    return this.createRootSourceModelNameBySwitchViewToken(switchViewToken).pipe(
      withLatestFrom(this.getQueryNameBySwitchViewToken(switchViewToken)),
      map(([rootSourceModelName, queryName]) => (rootSourceModelName ? rootSourceModelName : queryName))
    );
  }

  getQueryNameBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getQueryNameBySwitchViewToken(switchViewToken));
  }

  getIsFallbackDataBySwitchViewToken(switchViewToken: string) {
    return this.store
      .select(getDatasetResultByToken(switchViewToken))
      .pipe(map((datasetResult) => datasetResult?.isFallbackData));
  }

  getIsPurgedBySwitchViewToken(switchViewToken: string) {
    return this.store
      .select(getDatasetResultByToken(switchViewToken))
      .pipe(map((datasetResult) => datasetResult?.isPurged));
  }

  getDatasourceIdBySwitchViewToken(switchViewToken: string) {
    return this.store
      .select(getDatasetResultByToken(switchViewToken))
      .pipe(map((datasetResult) => datasetResult?.datasourceId));
  }

  getJsonUrlBySwitchViewToken(switchViewToken: string) {
    return this.store.select(getJsonUrlBySwitchViewToken(switchViewToken));
  }

  getModelFormatsByQueryToken(queryToken: string) {
    return this.store.select(getModelFormatsByQueryToken(queryToken));
  }

  /**
   * Action dispatchers
   */

  loadExplorationDataView(dataView: SwitchViewDomain.DataView) {
    this.store.dispatch(
      SwitchViewActions.loadExplorationDataView({
        dataView,
      })
    );
  }

  loadVisualizationDataView(dataView: SwitchViewDomain.DataView) {
    this.store.dispatch(
      SwitchViewActions.loadVisualizationDataView({
        dataView,
      })
    );
  }

  loadGfDataViews(dataViews: ReadonlyArray<SwitchViewDomain.DataView>) {
    this.store.dispatch(
      SwitchViewActions.loadGfDataViews({
        dataViews,
      })
    );
  }

  loadReportViewDataViews(dataViews: ReadonlyArray<SwitchViewDomain.DataView>) {
    this.store.dispatch(
      SwitchViewActions.loadReportViewDataViews({
        dataViews,
      })
    );
  }

  startPolling(accountId: string, reportToken: string, reportRunToken: string, viewTokens: ReadonlyArray<string>) {
    this.store.dispatch(
      SwitchViewActions.startPolling({
        accountId,
        reportToken,
        tokens: viewTokens,
        reportRunId: reportRunToken,
      })
    );
  }

  /**
   * The editor polling flow is different from the view polling in that it is dependent
   * on the user facade to get the accountId.
   */
  startEditorPolling(reportToken: string, reportRunToken: string, viewTokens: ReadonlyArray<string>) {
    this.store.dispatch(
      SwitchViewActions.startEditorPolling({
        reportToken,
        switchViewTokens: viewTokens,
        reportRunId: reportRunToken,
      })
    );
  }

  /**
   * When a query is run, we need to manually set the results to what we would expect for
   * a pending result synchronously. This triggers all related effects that depend on
   * `runReady` going back to false until the new data is reduced and it will go back
   * to true. Otherwise, it won't actually change, and won't actually trigger those
   * effects to rerun.
   */
  setPendingDatasetResultByQueryToken(queryToken: string) {
    this.createSwitchViewTokenByQueryToken(queryToken)
      .pipe(filter(isPresent), first())
      .subscribe((switchViewToken) => {
        this.store.dispatch(
          SwitchViewActions.setPendingDatasetResult({
            switchViewToken,
          })
        );
      });
  }

  setPendingAllLocalDatasetResults() {
    this.localSwitchViewTokens$.pipe(filter(isPresent), first()).subscribe((switchViewTokens) => {
      this.store.dispatch(
        SwitchViewActions.setPendingDatasetResults({
          switchViewTokens,
        })
      );
    });
  }

  // Temporary facade helper to dispatch from a legacy service. This will probably be
  // unnecessary once the createReportRun action and flow lives in Angular in the future.
  deleteSwitchViewResourcesFromCache(viewTokens: string[]) {
    this.store.dispatch(
      SwitchViewActions.deleteSwitchViewResourcesFromCache({
        switchViewTokens: viewTokens,
      })
    );
  }

  /**
   * Init the flow for the view details data for a query.
   */
  queryViewDetailsResults(queryToken: string) {
    this.store.dispatch(
      SwitchViewActions.queryRawDataset({
        queryToken,
      })
    );
  }

  /**
   * Init the flow for the view details data for a query using legacy state.
   *
   * This method is only used to query results for the legacy flamingo-grid component
   * and uses the legacy sqlResults state for paging.
   */
  queryViewDetailsResultsAngularJs(queryToken: string) {
    this.store.dispatch(
      SwitchViewActions.queryRawDatasetAngularJs({
        queryToken,
      })
    );
  }

  /**
   * Init the flow for the view details data for a query with calcs.
   */
  queryDetailsResultsWithCalcs(queryToken: string) {
    this.store.dispatch(
      SwitchViewActions.queryDatasetWithCalcs({
        queryToken,
      })
    );
  }

  openCalcEditor(
    switchViewToken: string,
    viewName: string,
    viewMode: 'static' | 'edit',
    calc = undefined,
    readOnly = false,
    duplicate = false,
    isImportedDataset = false,
    canShowFieldDescription = false
  ) {
    this.store.dispatch(
      SwitchViewActions.openCalcEditor({
        switchViewToken,
        viewName,
        viewMode,
        calc,
        readOnly,
        duplicate,
        isImportedDataset,
        canShowFieldDescription,
      })
    );
  }

  deleteCalcField(attr: FlamingoTypes.ModeCalc, token: string, queryToken: string) {
    this.store.dispatch(
      SwitchAttrsActions.deleteSwitchViewAttr({
        attr,
        token,
        queryToken,
      })
    );
  }

  getEditorSwitchViews(reportToken: string) {
    this.store.dispatch(SwitchViewActions.loadEditorViews({ reportToken }));
  }

  setSwitchViewRef(refId: string, switchViewToken: string) {
    this.store.dispatch(SwitchViewActions.setRef({ refId, switchViewToken }));
  }

  setSwitchViewRefs(refs: ReadonlyArray<{ refId: string; switchViewToken: string }>) {
    this.store.dispatch(SwitchViewActions.setRefs({ refs }));
  }

  deleteSwitchViewRef(refId: string) {
    this.store.dispatch(SwitchViewActions.deleteRef({ refId }));
  }

  deleteSwitchViewRefs(refIds: ReadonlyArray<string>) {
    this.store.dispatch(SwitchViewActions.deleteRefs({ refIds }));
  }

  // Temporary method to keep local state in sync with the legacy state
  // after the deleting a local query from new dataset components
  deleteEditorSwitchView(token: string) {
    this.createSwitchViewTokenByQueryToken(token)
      .pipe(filter(isPresent), first())
      .subscribe((switchViewToken) => {
        this.store.dispatch(SwitchViewActions.deleteSwitchViewLocalQuery({ switchViewToken }));
      });
  }

  // Temporary method to keep local state in sync with the legacy state
  // after renaming a local query from new dataset components
  updateLocalQuerySwitchViewTitle(token: string, title: string) {
    this.createSwitchViewTokenByQueryToken(token)
      .pipe(filter(isPresent), first())
      .subscribe((switchViewToken) => {
        this.store.dispatch(SwitchViewActions.updateLocalQuerySwitchViewTitle({ switchViewToken, title }));
      });
  }

  // Temporary method to add a temporary local query entity in the editor browser
  // before the raw_query gets run for the first time
  addTemporaryLocalQuery(queryName: string, queryToken: string, isMetric: boolean) {
    this.store.dispatch(SwitchViewActions.addTemporaryLocalQuery({ queryName, queryToken, isMetric }));
  }

  // Hydrate temporary local query with newly create queryToken
  updateTemporaryLocalQueryToken(tempQueryToken: string, queryToken: string) {
    this.store.dispatch(SwitchViewActions.updateTemporaryLocalQueryToken({ tempQueryToken, queryToken }));
  }

  removeDatasetFromTab(accountId: string, reportId: string, switchViewToken: string) {
    this.store.dispatch(SwitchViewActions.removeDatasetFromDatasetTab({ accountId, reportId, switchViewToken }));
  }

  removeDatasetFromSideNav(accountId: string, reportId: string, switchViewToken: string) {
    this.store.dispatch(SwitchViewActions.removeDatasetFromSideNav({ accountId, reportId, switchViewToken }));
  }

  clearDatasetFailure(switchViewToken: string, error: null) {
    this.store.dispatch(SwitchViewActions.clearDatasetFailure({ switchViewToken, error }));
  }

  datasetRefreshButtonClicked(reportToken: string, switchViewToken: string) {
    this.store.dispatch(
      SwitchViewActions.datasetRefreshButtonClicked({
        reportToken: reportToken,
        switchViewToken: switchViewToken,
      })
    );
  }

  datasetRefreshAllClicked(reportToken: string, switchViewTokens: string[]) {
    this.store.dispatch(
      SwitchViewActions.datasetRefreshAllClicked({
        reportToken: reportToken,
        switchViewTokens: switchViewTokens,
      })
    );
  }

  getDatasetWithSwitchViewToken(accountId: string, datasetToken: string) {
    return this.switchViewClientService.getDataset(accountId, datasetToken);
  }

  openDuplicateDialog(onDuplicateConfirm: () => void, isSecretExist: boolean, isPublished: boolean): void {
    this.store.dispatch(SwitchViewActions.openDuplicateDialog({ onDuplicateConfirm, isSecretExist, isPublished }));
  }
}
